import { useEffect, useState } from "react";
import {
  VacanciesCard,
  VacanciesCardButton,
  VacanciesCardContainer,
  VacanciesCardTitle,
  VacanciesContainer,
  VacanciesFilterCheckbox,
  VacanciesFilterCheckboxContainer,
  VacanciesFilterCheckboxLabel,
  VacanciesFilterCheckboxWrapper,
  VacanciesFilterContainer,
  VacanciesFilterHeader,
  VacanciesFilterInput,
  VacanciesFilterLabelHeader,
  VacanciesFilterRange,
  VacanciesFilterRangeContainer,
  VacanciesFilterRangeValue,
  VacanciesFilterText,
  VacanciesLocationInput,
  VacanciesOverviewContainer,
  VacanciesOverviewOption,
  VacanciesOverviewOptionsContainer,
  VacanciesProvincesInput,
  VacanciesTextContainer,
  VacanciesWrapper,
  VacancyIcon,
  VacancyIconWrapper,
} from "./VacanciesStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faClock,
  faEuroSign,
  faGraduationCap,
  faList,
  faLocationDot,
  faMap,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"; // If using Leaflet
import "leaflet/dist/leaflet.css"; // Leaflet styles
import L from "leaflet";
import { smartAPI } from "../api/Smart";

function Vacancies() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"

  // Define custom marker icon
  const customIcon = L.icon({
    iconUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png", // Use Leaflet's default icon
    iconSize: [25, 41], // Size of the icon
    iconAnchor: [12, 41], // Point of the icon that corresponds to marker's location
    popupAnchor: [1, -34], // Point from which the popup should open relative to the icon
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png", // Default shadow
    shadowSize: [41, 41], // Size of the shadow
  });
  const searchParamQuery = queryParams.get("search") || "";
  const provinceParamQuery = queryParams.get("province") || "";

  const [vacancies, setVacancies] = useState([]);

  const [hour, setHour] = useState(40);
  const [year, setYear] = useState(4);
  const [locationQuery, setLocationQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [provinceQuery, setProvinceQuery] = useState("alle");
  const [selectedSectors, setSelectedSectors] = useState({
    gehandicaptenzorg: true,
    ouderenzorg: true,
    geestelijkeGezondheidszorg: true,
    jeugdzorg: true,
    allezorg: true,
  });
  const [selectedContracts, setSelectedContracts] = useState({
    detachering: true,
    zzp: true,
    uitzendkracht: true,
    oproep: true,
  });
  const [selectedEducation, setSelectedEducation] = useState({
    mbo2: true,
    mbo3: true,
    mbo4: true,
    hbo: true,
  });

  // Scroll to the vacancies section when the search query changes
  useEffect(() => {
    const vacanciesElement = document.getElementById("vacancies-section");
    if (vacanciesElement && searchParamQuery !== "") {
      vacanciesElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.search, searchParamQuery]);

  useEffect(() => {
    fetchVacancies();
  }, []);

  const fetchVacancies = async () => {
    try {
      const response = await smartAPI({}, "GET", "vacancies");
      if (response.status === 200) {
        setVacancies(response.data);
      } else {
      }
    } catch (error) {
    } finally {
    }
  };

  // Handle the education filter
  function handleEducationChange(e) {
    const { name, checked } = e.target;
    setSelectedEducation((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }

  // Handle contract filter
  function handleContractChange(e) {
    const { name, checked } = e.target;
    setSelectedContracts((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }

  // Handle sector filter
  function handleSectorChange(e) {
    const { name, checked } = e.target;
    setSelectedSectors((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }

  // Handle location filter
  function handleLocationQuery(e) {
    setLocationQuery(e.target.value);
  }

  // Handle search filter
  function handleSearchChange(e) {
    setSearchQuery(e.target.value);
  }

  // Handle province filter
  function handleProvinceChange(e) {
    setProvinceQuery(e.target.value);
  }

  // Handle experience year slider change
  function handleYearSliderChange(e) {
    setYear(e.target.value);
  }

  // Handle work hour slider change
  function handleHourSliderChange(e) {
    setHour(e.target.value);
  }

  // Calculate the percentage for the background fill for the slider
  function getHourFillPercentage(hour) {
    return ((hour - 1) / (40 - 1)) * 100;
  }

  // Helper function to check if the selected hours are within the range
  function isWithinHoursRange(vacancyHours, selectedHours) {
    const vacancyHour = parseInt(vacancyHours, 10);
    return vacancyHour <= selectedHours;
  }

  // Filter vacancies into a filtered array
  const filteredVacancies = vacancies.filter((vacancy) => {
    const matchesSearch =
      vacancy.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vacancy.short_description
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesLocation =
      locationQuery === "" ||
      vacancy.location?.toLowerCase().includes(locationQuery.toLowerCase());

    const matchesProvince =
      provinceQuery === "alle" ||
      (vacancy.province &&
        vacancy.province?.toLowerCase() === provinceQuery.toLowerCase());

    const matchesContracts = Object.keys(selectedContracts)
      .filter((contract) => selectedContracts[contract])
      .some((contract) => vacancy.contract?.toLowerCase().includes(contract));

    const matchesEducation = Object.keys(selectedEducation)
      .filter((education) => selectedEducation[education])
      .some((education) =>
        vacancy.education
          ?.toLowerCase()
          .replace(/\s+/g, "")
          .includes(education.toLowerCase().replace(/\s+/g, ""))
      );

    const matchesParamSearch =
      vacancy.title?.toLowerCase().includes(searchParamQuery?.toLowerCase()) ||
      vacancy.short_description
        ?.toLowerCase()
        .includes(searchParamQuery.toLowerCase());

    const matchesParamProvince =
      provinceParamQuery === "" ||
      (vacancy.province &&
        vacancy.province.toLowerCase() === provinceParamQuery.toLowerCase());

    const matchesSectors =
      selectedSectors.allezorg ||
      Object.keys(selectedSectors)
        .filter((sector) => sector !== "allezorg")
        .some(
          (sector) =>
            selectedSectors[sector] &&
            (vacancy.title?.toLowerCase().includes(sector) ||
              vacancy.short_description?.toLowerCase().includes(sector))
        );

    const matchesExperience = vacancy.experience <= year;

    const matchesHours = isWithinHoursRange(vacancy.hours, hour);

    return (
      matchesSearch &&
      matchesHours &&
      matchesProvince &&
      matchesLocation &&
      matchesContracts &&
      matchesExperience &&
      matchesEducation &&
      matchesParamSearch &&
      matchesParamProvince &&
      matchesSectors
    );
  });

  return (
    <VacanciesContainer id="vacancies-section">
      <VacanciesTextContainer>
        <p style={{ fontSize: "2rem" }}>Vind wat bij jou past</p>
        <h1 style={{ fontSize: "2.5rem" }}>
          Bekijk onze openstaande vacatures
        </h1>
      </VacanciesTextContainer>
      <VacanciesWrapper>
        <VacanciesFilterContainer>
          <VacanciesFilterHeader>Filter vacatures</VacanciesFilterHeader>
          <VacanciesFilterText>
            Zoek naar een vacature die bij jou past
          </VacanciesFilterText>
          <VacanciesFilterInput
            placeholder="Zoek op functie of trefwoord"
            value={searchQuery}
            onChange={handleSearchChange}
          />

          <VacanciesFilterLabelHeader>Locatie</VacanciesFilterLabelHeader>
          <VacanciesProvincesInput
            value={provinceQuery}
            onChange={handleProvinceChange}
          >
            <option value="alle">Alle provincies</option>
            <option value="groningen">Groningen</option>
            <option value="friesland">Friesland</option>
            <option value="drenthe">Drenthe</option>
            <option value="overijssel">Overijssel</option>
            <option value="flevoland">Flevoland</option>
            <option value="gelderland">Gelderland</option>
            <option value="utrecht">Utrecht</option>
            <option value="noord-holland">Noord-Holland</option>
            <option value="zuid-holland">Zuid-Holland</option>
            <option value="zeeland">Zeeland</option>
            <option value="noord-brabant">Noord-Brabant</option>
            <option value="limburg">Limburg</option>
          </VacanciesProvincesInput>

          <VacanciesLocationInput
            value={locationQuery}
            onChange={handleLocationQuery}
          >
            <option value="">Alle steden</option>
            <option value="Arnhem">Arnhem</option>
            <option value="Oosterhout">Oosterhout</option>
            <option value="Waalwijk">Waalwijk</option>
            <option value="Waalwijk en omgeving">Waalwijk en omgeving</option>
            <option value="Tilburg">Tilburg</option>
            <option value="Tilburg en omgeving">Tilburg en omgeving</option>
            <option value="Breda en omgeving">Breda en omgeving</option>
            <option value="Eindhoven">Eindhoven</option>
            <option value="Kaatsheuvel">Kaatsheuvel</option>
          </VacanciesLocationInput>

          <VacanciesFilterLabelHeader>Sectoren</VacanciesFilterLabelHeader>
          <VacanciesFilterCheckboxContainer>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="allezorg"
                checked={selectedSectors.allezorg}
                onChange={handleSectorChange}
              />
              <VacanciesFilterCheckboxLabel>
                Alle zorg
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="gehandicaptenzorg"
                checked={selectedSectors.gehandicaptenzorg}
                onChange={handleSectorChange}
              />
              <VacanciesFilterCheckboxLabel>
                Gehandicaptenzorg
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="ouderenzorg"
                checked={selectedSectors.ouderenzorg}
                onChange={handleSectorChange}
              />
              <VacanciesFilterCheckboxLabel>
                Ouderenzorg
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="geestelijkeGezondheidszorg"
                checked={selectedSectors.geestelijkeGezondheidszorg}
                onChange={handleSectorChange}
              />
              <VacanciesFilterCheckboxLabel>
                Geestelijke Gezondheidszorg
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="jeugdzorg"
                checked={selectedSectors.jeugdzorg}
                onChange={handleSectorChange}
              />
              <VacanciesFilterCheckboxLabel>
                Jeugdzorg
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
          </VacanciesFilterCheckboxContainer>

          <VacanciesFilterLabelHeader>Uren per week</VacanciesFilterLabelHeader>
          <VacanciesFilterRangeContainer>
            <VacanciesFilterRange
              type="range"
              min="1"
              max="40"
              value={hour}
              onChange={handleHourSliderChange}
              style={{
                background: `linear-gradient(to right, #17224d ${getHourFillPercentage(
                  hour
                )}%, #d3d3d3 ${getHourFillPercentage(hour)}%)`,
              }}
            />
          </VacanciesFilterRangeContainer>
          <VacanciesFilterRangeValue>
            1 uur - {hour} uur
          </VacanciesFilterRangeValue>

          <VacanciesFilterLabelHeader>Dienstverband</VacanciesFilterLabelHeader>
          <VacanciesFilterCheckboxContainer>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="detachering"
                checked={selectedContracts.detachering}
                onChange={handleContractChange}
              />
              <VacanciesFilterCheckboxLabel>
                Detachering
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="zzp"
                checked={selectedContracts.zzp}
                onChange={handleContractChange}
              />
              <VacanciesFilterCheckboxLabel>ZZP</VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="uitzendkracht"
                checked={selectedContracts.uitzendkracht}
                onChange={handleContractChange}
              />
              <VacanciesFilterCheckboxLabel>
                Uitzendkracht
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="oproep"
                checked={selectedContracts.oproep}
                onChange={handleContractChange}
              />
              <VacanciesFilterCheckboxLabel>
                Oproep / Flexpool
              </VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
          </VacanciesFilterCheckboxContainer>

          <VacanciesFilterLabelHeader>
            Werkervaring in jaren
          </VacanciesFilterLabelHeader>
          <VacanciesFilterRangeContainer>
            <VacanciesFilterRange
              type="range"
              min="0"
              max="10"
              value={year}
              onChange={handleYearSliderChange}
              style={{
                background: `linear-gradient(to right, #17224d ${
                  year * 10
                }%, #d3d3d3 ${year * 10}%)`,
              }}
            />
          </VacanciesFilterRangeContainer>
          <VacanciesFilterRangeValue>
            0 jaar - {year} jaar
          </VacanciesFilterRangeValue>

          <VacanciesFilterLabelHeader>
            Opleidingsniveau
          </VacanciesFilterLabelHeader>
          <VacanciesFilterCheckboxContainer>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="mbo2"
                checked={selectedEducation.mbo2}
                onChange={handleEducationChange}
              />
              <VacanciesFilterCheckboxLabel>MBO 2</VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="mbo3"
                checked={selectedEducation.mbo3}
                onChange={handleEducationChange}
              />
              <VacanciesFilterCheckboxLabel>MBO 3</VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="mbo4"
                checked={selectedEducation.mbo4}
                onChange={handleEducationChange}
              />
              <VacanciesFilterCheckboxLabel>MBO 4</VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
            <VacanciesFilterCheckboxWrapper>
              <VacanciesFilterCheckbox
                type="checkbox"
                name="hbo"
                checked={selectedEducation.hbo}
                onChange={handleEducationChange}
              />
              <VacanciesFilterCheckboxLabel>HBO</VacanciesFilterCheckboxLabel>
            </VacanciesFilterCheckboxWrapper>
          </VacanciesFilterCheckboxContainer>
        </VacanciesFilterContainer>

        <VacanciesOverviewContainer>
          <VacanciesOverviewOptionsContainer>
            <p>{filteredVacancies.length} vacatures</p>
            <div>
              <VacanciesOverviewOption
                onClick={() => setViewMode("list")}
                $isActive={viewMode === "list"}
              >
                <FontAwesomeIcon
                  icon={faList}
                  style={{ marginRight: "10px" }}
                />
                <span>Lijst</span>
              </VacanciesOverviewOption>
              <VacanciesOverviewOption
                onClick={() => setViewMode("map")}
                $isActive={viewMode === "map"}
              >
                <FontAwesomeIcon icon={faMap} style={{ marginRight: "10px" }} />
                <span>Kaart</span>
              </VacanciesOverviewOption>
            </div>
          </VacanciesOverviewOptionsContainer>

          {viewMode === "list" ? (
            <VacanciesCardContainer>
              {filteredVacancies.map((vacancy) => (
                <VacanciesCard key={vacancy.id}>
                  <VacanciesCardTitle>{vacancy.title}</VacanciesCardTitle>
                  <p style={{ marginBottom: "20px" }}>
                    {vacancy.short_description}
                  </p>
                  <p>
                    <VacancyIconWrapper>
                      <VacancyIcon icon={faLocationDot} />
                    </VacancyIconWrapper>
                    {vacancy.location}
                  </p>
                  <p>
                    <VacancyIconWrapper>
                      <VacancyIcon icon={faClock} />
                    </VacancyIconWrapper>
                    {vacancy.hours}
                  </p>
                  <p>
                    <VacancyIconWrapper>
                      <VacancyIcon icon={faEuroSign} />
                    </VacancyIconWrapper>
                    {vacancy.salary}
                  </p>
                  <p>
                    <VacancyIconWrapper>
                      <VacancyIcon icon={faCircleInfo} />
                    </VacancyIconWrapper>
                    {vacancy.categories}
                  </p>
                  <p>
                    <VacancyIconWrapper>
                      <VacancyIcon icon={faGraduationCap} />
                    </VacancyIconWrapper>
                    {vacancy.education}
                  </p>
                  <Link
                    to={`/healthcare/vacancy/${vacancy.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <VacanciesCardButton>Bekijk vacature</VacanciesCardButton>
                  </Link>
                </VacanciesCard>
              ))}
            </VacanciesCardContainer>
          ) : (
            <div
              style={{
                border: "2px solid #17224d",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
                zIndex: 1,
              }}
            >
              <MapContainer
                center={[52.3676, 4.9041]}
                zoom={8}
                style={{
                  height: "700px",
                  width: "100%",
                  zIndex: 1,
                }}
                attributionControl={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                  style={{
                    zIndex: 1,
                  }}
                />
                {filteredVacancies.map((vacancy, index) => (
                  <Marker
                    key={index}
                    position={[vacancy.latitude, vacancy.lengtitude]}
                    icon={customIcon}
                  >
                    <Popup>
                      <Link to="/">
                        <img
                          src="/assets/logo.jpg"
                          alt="Logo"
                          style={{ height: "20px", marginLeft: "25%" }}
                        />
                      </Link>
                      <br />
                      <br />
                      <div style={{ textAlign: "center" }}>
                        <b style={{ color: "#17224d" }}>{vacancy.title}</b> -{" "}
                        <span style={{ color: "#17224d" }}>
                          {vacancy.location}
                        </span>
                      </div>
                      <Link
                        to={`/healthcare/vacancy/${vacancy.id}`}
                        style={{ textDecoration: "none" }}
                      >
                        <VacanciesCardButton>
                          Bekijk vacature
                        </VacanciesCardButton>
                      </Link>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </VacanciesOverviewContainer>
      </VacanciesWrapper>
    </VacanciesContainer>
  );
}

export default Vacancies;
